import type { ChangeEvent, UIEvent } from 'react';
import React, { useState, useMemo, useEffect } from 'react';

import { useGetDepartmentsInfinite, useGetUser, usePatchUser } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { CheckIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useAttachUserDepartmentsTranslation } from '../i18n';
import type { AttachUserDepartmentsProps } from '../types';
import st from './AttachUserDepartmentsDropdown.module.sass';

const POPUP_WIDTH = 280;
const POPUP_MAX_HEIGHT = 360;
const SCROLL_THRESHOLD = 8;
const PAGE_SIZE = 10;

const displayName = 'AttachUserDepartmentsDropdown';
const testid = buildTestid(displayName);

interface AttachUserDepartmentsDropdownProps extends AttachUserDepartmentsProps {
  children: React.ReactElement;
  disabled?: boolean;
}

function AttachUserDepartmentsDropdown({
  children,
  userId,
  disabled = false,
}: AttachUserDepartmentsDropdownProps): React.JSX.Element {
  const { t } = useAttachUserDepartmentsTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => {
      setQuery(value);
    }, 300),
    [],
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const user = useGetUser(userId);
  const userDepartmentIds = user?.data?.department_ids ?? [];

  const getDepartments = useGetDepartmentsInfinite(
    {
      page: 1,
      page_size: PAGE_SIZE,
      name: query || undefined,
    },
    {
      query: {
        enabled: isOpen,
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => (
          (lastPage?.departments?.length ?? 0) < PAGE_SIZE ? undefined : allPages.length + 1
        ),
      },
    },
  );

  const patchUser = usePatchUser({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        setIsOpen(false);
      },
      onError: () => {
        toast.error(t('on_error'));
      },
    },
  });

  const departments = useMemo(() => {
    if (!getDepartments.data?.pages) return [];
    return getDepartments.data?.pages.flatMap((page) => page.departments ?? []);
  }, [getDepartments.data]);

  const isLoading = isOpen && getDepartments.isLoading && user.isLoading;
  const hasError = getDepartments.isError;
  const isDisabled = disabled || patchUser.isPending;

  const handleAttachDepartment = (departmentId: DepartmentId): void => {
    patchUser.mutate({
      userId,
      data: {
        department_ids: [...userDepartmentIds, departmentId],
      },
    });
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      debouncedSetQuery.cancel();
      setInputValue('');
      setQuery('');
    }
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
    debouncedSetQuery(e.target.value);
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD;
    if (isNearBottom && getDepartments.hasNextPage && !getDepartments.isFetchingNextPage) {
      getDepartments.fetchNextPage();
    }
  };

  return (
    <DropdownMenu.Root
      modal={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <DropdownMenu.Trigger
        disabled={isDisabled}
        render={children}
      />
      <DropdownMenu.Portal>
        <DropdownMenu.Positioner className={st.Positioner}>
          <DropdownMenu.Popup
            onScroll={handleScroll}
            style={{
              maxHeight: POPUP_MAX_HEIGHT,
              overflowY: 'auto',
              width: POPUP_WIDTH,
            }}
            {...testid()}
          >
            <DropdownMenu.SearchItem
              onChange={handleQueryChange}
              placeholder={t('search_placeholder')}
              size="small"
              value={inputValue}
              {...testid()}
            />
            {!!isLoading && (
              <DropdownMenu.EmptyItem {...testid('loading')}>{t('loading')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !!hasError && (
              <DropdownMenu.EmptyItem {...testid('load-error')}>{t('load_error')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !hasError && departments.length === 0 && (
              <DropdownMenu.EmptyItem {...testid('empty')}>{t('empty')}</DropdownMenu.EmptyItem>
            )}
            {!isLoading && !hasError && departments.map(({ department_id, name }) => (
              <DropdownMenu.Item
                {...testid(department_id)}
                decorationRight={userDepartmentIds.includes(department_id) && <CheckIcon />}
                disabled={patchUser.isPending || userDepartmentIds.includes(department_id)}
                key={department_id}
                onClick={() => handleAttachDepartment(department_id)}
              >
                {name}
              </DropdownMenu.Item>
            ))}
            {!isLoading && !hasError && !!getDepartments.isFetchingNextPage && (
              <DropdownMenu.EmptyItem {...testid('loading-more')}>{t('loading')}</DropdownMenu.EmptyItem>
            )}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

AttachUserDepartmentsDropdown.displayName = displayName;

export { AttachUserDepartmentsDropdown };
